import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
// Import Types
import type { ChartFilterModel } from './types';
// Import Enums
import { MenuItemIdEnum, TrackingModeEnum } from 'enums';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import UI Components
import { DatePickerInput, Dropdown } from '@geneui/components';
// Import Components
import MenuItemDropdown from 'components/MenuItemDropdown';
// Import Services
import { PromotionHttpService, TemplateHttpService, UtilsHttpService } from 'services/http';
// Import Helpers
import { getDate, getPromotionRequestBody, getTemplateRequestBody } from './helper';
// Import Constants
import { DeliveryMethodTypes, l } from 'constants/common';

interface Props {
    parentDate: string[];
    onChange: (data: ChartFilterModel) => void;
}

const Filters: FC<Props> = ({ parentDate, onChange }) => {
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();

    const [filterModel, setFilterModel] = useState<ChartFilterModel>({
        startDate: parentDate[0],
        endDate: parentDate[1],
        deliveryMethods: [],
        promotions: [],
        templateIds: [],
        trackedLinkIds: [],
    });

    const { partnerDateFormat } = usePartnerDateFormats();

    const [channels, setChannels] = useState<{ value: string; label: string }[]>([]);
    const [isLoadingChannels, setIsLoadingChannels] = useState(false);

    const [promotions, setPromotions] = useState<{ value: string; label: string }[]>([]);
    const [isLoadingPromotions, setIsLoadingPromotions] = useState(false);

    const [templates, setTemplates] = useState<{ value: string; label: string }[]>([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

    const { getAvailableDeliveryMethodsRequest, getPromotionListRequest, getTemplateListRequest } =
        useCancelableRequests(
            {
                getAvailableDeliveryMethodsRequest: UtilsHttpService.getAvailableDeliveryMethods,
                getPromotionListRequest: PromotionHttpService.getPromotionList,
                getTemplateListRequest: TemplateHttpService.getTemplateList,
            },
            { takeLatest: ['getPromotionListRequest', 'getTemplateListRequest'] },
        );

    const datePickerValue = useMemo(() => {
        return [filterModel.startDate, filterModel.endDate];
    }, [filterModel.startDate, filterModel.endDate]);

    const fetchPromotions = useCallback(
        (channels: string[]) => {
            setIsLoadingPromotions(true);
            doPostRequest(getPromotionListRequest.request, {
                requestBody: getPromotionRequestBody(channels),
                successCallback: (data) => {
                    const options = data.Data.map((item) => ({
                        value: item.Id,
                        label: item.Name,
                    }));

                    setPromotions(options);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsLoadingPromotions(false);
                }
            });
        },
        [doPostRequest, getPromotionListRequest],
    );

    const fetchTemplates = useCallback(
        (channels: string[], promotions: string[]) => {
            setIsLoadingTemplates(true);
            doPostRequest(getTemplateListRequest.request, {
                requestBody: getTemplateRequestBody(channels, promotions),
                successCallback: (data) => {
                    const options = data.Data.map((item) => ({
                        value: item.TemplateId.toString(),
                        label: item.Name,
                    }));

                    setTemplates(options);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsLoadingTemplates(false);
                }
            });
        },
        [doPostRequest, getTemplateListRequest],
    );

    const handleDatePickerChange = (e) => {
        const date = getDate(e);

        if (date[0] === filterModel.startDate && date[1] === filterModel.endDate) {
            return;
        }

        const newFilterModel: ChartFilterModel = {
            ...filterModel,
            startDate: date[0],
            endDate: date[1],
        };

        setFilterModel(newFilterModel);
        onChange(newFilterModel);
    };

    const handleChannelChange = (data: { value: string; label: string }[]) => {
        const newSelection = data.map((d) => d.value);
        fetchPromotions(newSelection);

        const newFilterModel: ChartFilterModel = {
            ...filterModel,
            deliveryMethods: newSelection,
            promotions: [],
            templateIds: [],
            trackedLinkIds: [],
        };
        setFilterModel(newFilterModel);
        onChange(newFilterModel);
    };

    const handlePromotionChange = (data: { value: string; label: string }[]) => {
        const newSelection = data.map((d) => d.value);
        fetchTemplates(filterModel.deliveryMethods, newSelection);

        const newFilterModel: ChartFilterModel = {
            ...filterModel,
            promotions: newSelection,
            templateIds: [],
            trackedLinkIds: [],
        };
        setFilterModel(newFilterModel);
        onChange(newFilterModel);
    };

    const handleTemplateChange = (data: { value: string; label: string }[]) => {
        const newSelection = data.map((d) => d.value);
        const newFilterModel: ChartFilterModel = {
            ...filterModel,
            templateIds: newSelection,
            trackedLinkIds: [],
        };
        setFilterModel(newFilterModel);
    };

    const handleLinkChange = useCallback(
        (dropdownValues) => {
            if ('value' in dropdownValues) {
                const newFilterModel: ChartFilterModel = {
                    ...filterModel,
                    trackedLinkIds: dropdownValues.value ?? [],
                };
                setFilterModel(newFilterModel);
                onChange(newFilterModel);
            }
        },
        [filterModel, onChange],
    );

    const linkRequestAdditionalParams = useMemo(() => {
        return {
            TemplateId: filterModel.templateIds,
        };
    }, [filterModel.templateIds]);

    useEffect(() => {
        handleDatePickerChange(parentDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentDate]);

    const init = () => {
        setIsLoadingChannels(true);
        doPostRequest(getAvailableDeliveryMethodsRequest.request, {
            successCallback: (data) => {
                const options = data
                    .filter((method) => method.AllowTracking !== TrackingModeEnum.None)
                    .map((method) => ({
                        value: method.DeliveryMethod.toString(),
                        label: t(l[DeliveryMethodTypes[method.DeliveryMethod]]),
                    }));

                setChannels(options);
            },
        }).finally(() => setIsLoadingChannels(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <>
            <div className="campaign-metrics-wrap-c-h-d">
                <Dropdown
                    isMultiSelect={true}
                    label={t(l.Channels)}
                    labelAppearance="swap"
                    value={filterModel.deliveryMethods}
                    onChange={handleChannelChange}
                    isLoading={isLoadingChannels}
                    data={channels}
                    hasSearch={false}
                    placeholder={t(l.Channels)}
                    clearable={true}
                />
                <Dropdown
                    isMultiSelect={true}
                    label={t(l.Promotions)}
                    labelAppearance="swap"
                    value={filterModel.promotions}
                    onChange={handlePromotionChange}
                    isLoading={isLoadingPromotions}
                    data={promotions}
                    hasSearch={true}
                    placeholder={t(l.Promotions)}
                    clearable={true}
                    disabled={isEmpty(filterModel.deliveryMethods)}
                />
                <Dropdown
                    isMultiSelect={true}
                    label={t(l.Templates)}
                    labelAppearance="swap"
                    value={filterModel.templateIds}
                    onChange={handleTemplateChange}
                    isLoading={isLoadingTemplates}
                    data={templates}
                    hasSearch={true}
                    placeholder={t(l.Templates)}
                    clearable={true}
                    disabled={isEmpty(filterModel.promotions)}
                />
                <MenuItemDropdown
                    key={JSON.stringify(filterModel.templateIds)}
                    disabled={isEmpty(filterModel.templateIds)}
                    displayName={t(l.Links)}
                    menuItemId={MenuItemIdEnum.LinkTracker}
                    placeholder={t(l.Links)}
                    doInitialRequest={true}
                    isMultiselect={true}
                    extendTargetWidth={false}
                    additionalParams={linkRequestAdditionalParams}
                    getUpdate={handleLinkChange}
                    popoverAlign="start"
                />
                <DatePickerInput.WithRange
                    onChange={handleDatePickerChange}
                    label={t(l.TimePeriod)}
                    labelAppearance="swap"
                    value={datePickerValue}
                    format={partnerDateFormat}
                />
            </div>
        </>
    );
};

export default Filters;
